import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Icon } from './Controls';
import { addApprovedLinks, refreshApprovedLink, deleteDocument, listDocuments, originalFileUrl, uploadDocuments } from './api';

function formatTime(value) {
  if (!value) return '';
  return new Date(value).toLocaleString();
}

function statusClass(status) {
  if (status === 'ready') return 'text-success bg-success/10';
  if (status === 'failed') return 'text-danger bg-danger/10';
  if (status === 'uploading') return 'text-ink-muted bg-surface-muted';
  return 'text-warning bg-warning/10';
}

function statusLabel(status) {
  if (status === 'ready') return 'Ready';
  if (status === 'failed') return 'Failed';
  if (status === 'uploading') return 'Uploading';
  return 'Processing';
}

export default function KnowledgeBase() {
  const tabsId = useId();
  const [activeTab, setActiveTab] = useState('links');
  const [addMode, setAddMode] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewText, setPreviewText] = useState('');
  const [previewError, setPreviewError] = useState('');
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [linkUrl, setLinkUrl] = useState('');
  const [addingLink, setAddingLink] = useState(false);
  const uploadLock = useRef(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [dragging, setDragging] = useState(false);
  const [deletingId, setDeletingId] = useState('');
  const links = documents.filter((doc) => Boolean(doc.sourceUrl));
  const files = documents.filter((doc) => !doc.sourceUrl);
  const visibleDocuments = activeTab === 'links' ? links : files;

  function selectTab(tab) {
    setActiveTab(tab);
    setAddMode(null);
    setPreview(null);
  }

  function onTabKeyDown(event) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const tab = event.key === 'Home' ? 'links' : event.key === 'End' ? 'pdfs' : activeTab === 'links' ? 'pdfs' : 'links';
    selectTab(tab);
    document.getElementById(`${tabsId}-${tab}-tab`)?.focus();
  }

  function toggleAdd(mode) {
    setActiveTab(mode === 'link' ? 'links' : 'pdfs');
    setPreview(null);
    setAddMode(addMode === mode ? null : mode);
  }

  const refresh = useCallback(async () => {
    const data = await listDocuments();
    setDocuments(current => [...current.filter(doc => doc.local), ...(data.documents || [])]);
  }, []);

  useEffect(() => {
    refresh().catch((err) => setError(err.message)).finally(() => setLoading(false));
  }, [refresh]);

  useEffect(() => {
    if (!preview) return undefined;
    const controller = new AbortController();
    setPreviewText(''); setPreviewError('');
    fetch(originalFileUrl(preview.id), { credentials: 'include', signal: controller.signal }).then(async (response) => {
      if (!response.ok) throw new Error('Saved content is not available. Refresh this link and try again.');
      return response.text();
    }).then(setPreviewText).catch((err) => { if (!controller.signal.aborted) setPreviewError(err.message); });
    return () => controller.abort();
  }, [preview]);

  useEffect(() => {
    const pending = documents.some((doc) => doc.status === 'processing');
    if (!pending) return undefined;
    const timer = setInterval(() => {
      refresh().catch((err) => setError(err.message));
    }, 2000);
    return () => clearInterval(timer);
  }, [documents, refresh]);

  async function handleFiles(fileList) {
    const selectedFiles = [...fileList];
    if (!selectedFiles.length || uploadLock.current) return;
    uploadLock.current = true;
    const failures = [];
    const files = selectedFiles.filter(file => {
      const reason = !/\.(pdf|docx)$/i.test(file.name) ? 'Only PDF and DOCX files are supported.'
        : !file.size ? 'This file is empty.' : file.size > 15 * 1024 * 1024 ? 'Each file must be 15 MB or smaller.' : '';
      if (reason) failures.push(`${file.name}: ${reason}`);
      return !reason;
    });
    setActiveTab('pdfs');
    setError('');
    setNotice('');
    const locals = files.map((file, index) => ({
      id: `local-${Date.now()}-${index}`,
      filename: file.name,
      extension: file.name.split('.').pop()?.toLowerCase() || '',
      sizeBytes: file.size,
      status: 'uploading',
      uploadedAt: new Date().toISOString(),
      local: true
    }));
    setDocuments((current) => [...locals, ...current]);
    let accepted = 0;
    let completed = selectedFiles.length - files.length;
    setUploadProgress({ completed, total: selectedFiles.length });
    try {
      // Respect the server's eight-file request limit while accepting a larger selection.
      for (let offset = 0; offset < files.length; offset += 8) {
        const batch = files.slice(offset, offset + 8);
        const ids = new Set(locals.slice(offset, offset + 8).map(doc => doc.id));
        try {
          const data = await uploadDocuments(batch);
          (data.documents || []).forEach(doc => ids.add(doc.id));
          accepted += data.documents?.length || 0;
          for (const result of data.results || []) {
            if (!result.ok) failures.push(`${batch[result.index]?.name || 'File'}: ${result.message}`);
          }
          setDocuments(current => [...current.filter(doc => !ids.has(doc.id)), ...(data.documents || [])]);
        } catch (err) {
          failures.push(...batch.map(file => `${file.name}: ${err.message || 'Upload failed.'}`));
          setDocuments(current => current.filter(doc => !ids.has(doc.id)));
        }
        completed += batch.length;
        setUploadProgress({ completed, total: selectedFiles.length });
      }
      setNotice(`${accepted} of ${selectedFiles.length} files accepted. ${accepted ? 'Processing has started.' : 'No files were added.'}`);
      if (failures.length) setError(failures.join('\n'));
      try { await refresh(); }
      catch (err) { setError(current => [current, err.message].filter(Boolean).join('\n')); }
    } finally {
      uploadLock.current = false;
      setUploadProgress(null);
    }
  }

  async function onDelete(doc) {
    setDeletingId(doc.id);
    setError('');
    try {
      await deleteDocument(doc.id);
      setDocuments((current) => current.filter((item) => item.id !== doc.id));
      setNotice(`${doc.filename} was deleted and removed from retrieval.`);
    } catch (err) {
      setError(err.message || 'Could not delete that document.');
    } finally {
      setDeletingId('');
    }
  }

  async function addLink(event) {
    event.preventDefault();
    if (addingLink) return;
    const urls = [...new Set(linkUrl.split(/\r?\n/).map(line => line.trim()).filter(Boolean))];
    if (!urls.length || urls.length > 30) { setError('Enter between 1 and 30 links, one per line.'); return; }
    setAddingLink(true); setError(''); setNotice('');
    try {
      const data = await addApprovedLinks(urls);
      const accepted = data.results.filter(result => result.ok);
      const rejected = data.results.filter(result => !result.ok);
      setNotice(`${accepted.length} of ${urls.length} links accepted. ${accepted.length ? 'Processing has started.' : 'No links were added.'}`);
      setLinkUrl(rejected.map(result => urls[result.index]).join('\n'));
      if (!rejected.length) setAddMode(null);
      else setError(rejected.map(result => `${urls[result.index]}: ${result.message}`).join('\n'));
      await refresh();
    }
    catch (err) { setError(err.message); } finally { setAddingLink(false); }
  }
  async function refreshLink(id) {
    setError('');
    try { await refreshApprovedLink(id); await refresh(); } catch (err) { setError(err.message); }
  }
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap"><p className="text-sm text-ink-muted">{loading ? 'Loading sources…' : `${documents.length} sources`}</p><div className="flex gap-2"><button type="button" className="arts-pill arts-secondary" onClick={() => toggleAdd('link')} aria-expanded={addMode === 'link'}><Icon name="link" /> Add links</button><button type="button" className="arts-pill arts-secondary" onClick={() => toggleAdd('file')} aria-expanded={addMode === 'file'}><Icon name="file" /> Add files</button></div></div>
      <div className="arts-source-tabs" role="tablist" aria-label="Knowledge sources">
        {[{ key: 'links', label: 'Links', icon: 'link', count: links.length }, { key: 'pdfs', label: 'PDFs', icon: 'file', count: files.length }].map((tab) => (
          <button key={tab.key} id={`${tabsId}-${tab.key}-tab`} type="button" role="tab" aria-selected={activeTab === tab.key} aria-controls={`${tabsId}-${tab.key}-panel`} tabIndex={activeTab === tab.key ? 0 : -1} className="arts-source-tab" onClick={() => selectTab(tab.key)} onKeyDown={onTabKeyDown}>
            <Icon name={tab.icon} /> {tab.label} <span className="arts-source-count">{loading ? '…' : tab.count}</span>
          </button>
        ))}
      </div>
      <p className="text-xs text-ink-muted">{activeTab === 'links' ? 'Official webpages and linked PDFs.' : 'Uploaded PDFs and Word documents (DOCX).'}</p>
      {preview && <section className="arts-source-preview"><div className="flex justify-between gap-3 items-center"><h3 className="font-medium">{preview.filename}</h3><button type="button" className="arts-icon-button" aria-label="Close saved content" onClick={() => setPreview(null)}><Icon name="close" /></button></div><p className="text-xs text-ink-muted mb-4">Saved content · {formatTime(preview.fetchedAt)}</p>{previewError ? <p role="alert" className="text-danger text-sm">{previewError}</p> : <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed max-h-72 overflow-auto">{previewText || 'Loading saved content…'}</pre>}</section>}
      {addMode === 'link' && <section className="rounded-2xl border border-edge bg-surface p-5">
        <h2 className="text-lg font-semibold">Approved links</h2>
        <p id={`${tabsId}-link-help`} className="mt-1 text-sm text-ink-muted">Paste up to 30 official HKU links, one per line. Titles are filled in automatically.</p>
        <form onSubmit={addLink} className="mt-4 flex flex-col gap-3">
          <textarea aria-label="Official HKU links" aria-describedby={`${tabsId}-link-help`} placeholder={'https://…hku.hk/…\nhttps://…hku.hk/…'} required rows={6} disabled={addingLink} value={linkUrl} onChange={(event) => setLinkUrl(event.target.value)} className="min-w-0 w-full resize-y rounded-2xl border border-edge bg-canvas px-4 py-3 text-sm disabled:opacity-50" />
          <button disabled={addingLink || !linkUrl.trim()} className="self-end rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm disabled:opacity-50">{addingLink ? 'Adding…' : 'Add links'}</button>
        </form>
      </section>}
      {addMode === 'file' && <section
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          handleFiles(event.dataTransfer.files);
        }}
        className={`rounded-2xl border-2 border-dashed px-6 py-6 text-center bg-surface ${
          dragging ? 'border-primary bg-accent/20' : 'border-edge'
        }`}
      >
        <h2 className="text-lg font-semibold">Upload documents</h2>
        <p className="mt-2 text-sm text-ink-muted">Select or drop multiple PDFs or Word documents together. Up to 15 MB per file.</p>
        <label className={`mt-5 inline-flex rounded-full bg-primary px-4 py-2 text-sm text-primary-foreground ${uploadProgress ? 'opacity-50' : 'cursor-pointer'}`}>
          {uploadProgress ? 'Uploading…' : 'Select files'}
          <input
            type="file"
            accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            multiple
            disabled={Boolean(uploadProgress)}
            className="sr-only"
            onChange={(event) => {
              handleFiles(event.target.files);
              event.target.value = '';
            }}
          />
        </label>
      </section>}

      {uploadProgress && <p role="status" className="text-sm text-ink-muted">Uploading files: {uploadProgress.completed} of {uploadProgress.total} checked.</p>}
      {notice && <p role="status" className="text-sm text-success">{notice}</p>}
      {error && <p role="alert" className="text-sm text-danger whitespace-pre-wrap break-all">{error}</p>}

      {['links', 'pdfs'].map((tab) => <section key={tab} id={`${tabsId}-${tab}-panel`} role="tabpanel" aria-labelledby={`${tabsId}-${tab}-tab`} hidden={activeTab !== tab} tabIndex={0} className="arts-source-panel space-y-3" aria-busy={loading}>
        {activeTab === tab && <>

        {!loading && !visibleDocuments.length && (
          <p className="arts-source-empty text-sm text-ink-muted">{activeTab === 'links' ? 'No links yet. Add an official HKU link to get started.' : 'No uploaded documents yet. Add a PDF or DOCX file to get started.'}</p>
        )}
        {visibleDocuments.map((doc) => (
          <article key={doc.id} className="arts-source-row">
            <span className="arts-source-icon"><Icon name={doc.sourceUrl ? 'link' : 'file'} /></span>
            <div className="min-w-0 flex-1">
              <h3 className="font-medium break-words text-sm">{doc.filename}</h3>
              <p className="mt-1 text-sm text-ink-muted">
                {doc.sourceUrl ? 'Approved link' : (doc.extension || 'file').toUpperCase()}
                {doc.fetchedAt ? ` · Retrieved ${formatTime(doc.fetchedAt)}` : doc.uploadedAt ? ` · ${formatTime(doc.uploadedAt)}` : ''}
                {doc.pageCount ? ` · ${doc.pageCount} pages` : ''}
                {doc.chunkCount ? ` · ${doc.chunkCount} sections` : ''}
              </p>
              {doc.sourceUrl && <p className="mt-1 text-xs text-ink-muted break-all">{doc.sourceUrl}</p>}
              {doc.status === 'failed' && doc.errorMessage && (
                <p className="mt-2 text-sm text-danger">{doc.errorMessage}</p>
              )}
            </div>
            <div className="arts-source-actions">
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${statusClass(doc.status)}`}>
                {statusLabel(doc.status)}
              </span>
              {!doc.local && (
                <a
                  href={doc.sourceUrl || originalFileUrl(doc.id)}
                  target="_blank"
                  rel="noreferrer"
                  className="arts-pill arts-source-action"
                >
                  {doc.sourceUrl ? 'Visit' : 'Open'}
                </a>
              )}
              {doc.sourceUrl && (doc.hasSnapshot || doc.fetchedAt) && <>
                {doc.extension === 'html' && <button type="button" className="arts-pill arts-source-action" onClick={() => setPreview(doc)}>Preview</button>}
                <a href={originalFileUrl(doc.id)} download={doc.extension === 'html' ? `${doc.filename}.txt` : undefined} target="_blank" rel="noreferrer" className="arts-pill arts-source-action">{doc.extension === 'pdf' ? 'Saved PDF' : 'Download'}</a>
              </>}
              {doc.sourceUrl && <button type="button" disabled={doc.status === 'processing' && Date.now() - new Date(doc.updatedAt || doc.uploadedAt).getTime() < 10 * 60 * 1000} onClick={() => refreshLink(doc.id)} className="arts-pill arts-source-action disabled:opacity-40">{doc.status === 'failed' ? 'Retry' : 'Refresh'}</button>}
              {!doc.local && (
                <button
                  type="button"
                  onClick={() => onDelete(doc)}
                  disabled={deletingId === doc.id}
                  className="arts-pill arts-source-action text-danger disabled:opacity-50"
                >
                  {deletingId === doc.id ? 'Deleting…' : 'Delete'}
                </button>
              )}
            </div>
          </article>
        ))}
        </>}
      </section>)}
    </div>
  );
}
